import React, { useState, useEffect, useRef } from "react";
import { Spinner } from "./Spinner";
import { useSearch } from "../../hooks/useSearch";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import importScript from "../../hooks/importScript";
import FileUploadService from "../../services/FileUploadService";
import Cs from "../../services/CommonService";

const CDN_EDITORJS = "https://cdn.jsdelivr.net/npm/@editorjs/editorjs@2.29.1";
const CDN_HEADER           = "https://cdn.jsdelivr.net/npm/@editorjs/header@latest";
const CDN_LIST             = "https://cdn.jsdelivr.net/npm/@editorjs/list@latest";
const CDN_TABLE            = "https://cdn.jsdelivr.net/npm/@editorjs/table@latest";
const CDN_MARKER           = "https://cdn.jsdelivr.net/npm/@editorjs/marker@latest";
const CDN_IMAGE            = "https://cdn.jsdelivr.net/npm/@editorjs/image@latest";
const CDN_COLOR            = "https://cdn.jsdelivr.net/npm/editorjs-color-picker@1.0.8/dist/index.umd.js";
const CDN_PARA             = "https://cdn.jsdelivr.net/npm/@editorjs/paragraph@2.11.7/dist/paragraph.umd.min.js";
const CDN_TXT_BLOCKTUNE    = "https://cdn.jsdelivr.net/npm/editorjs-text-alignment-blocktune@latest";
const CDN_TEXT_STYLE       = "https://cdn.jsdelivr.net/npm/@skchawala/editorjs-text-style@latest";
const CDN_QUOTE            = "https://cdn.jsdelivr.net/npm/@editorjs/quote@latest";
const CDN_DELIMITER        = "https://cdn.jsdelivr.net/npm/@coolbytes/editorjs-delimiter@latest";
const CDN_CHECKLIST        = "https://cdn.jsdelivr.net/npm/@editorjs/checklist@latest";
const CDN_UNDERLINE        = "https://cdn.jsdelivr.net/npm/@editorjs/underline@latest";
const CDN_INLINE_CODE      = "https://cdn.jsdelivr.net/npm/@editorjs/inline-code@latest";
const CDN_NOTICE           = "https://cdn.jsdelivr.net/npm/editorjs-notice";
const CDN_TEXT_VARIANT     = "https://cdn.jsdelivr.net/npm/@editorjs/text-variant-tune@latest";
const CDN_INDENT_TUNE      = "https://cdn.jsdelivr.net/npm/editorjs-indent-tune/dist/bundle.js";
const CDN_WARNING          = "https://cdn.jsdelivr.net/npm/@editorjs/warning@latest";
const CDN_ALERT            = "https://cdn.jsdelivr.net/npm/editorjs-alert@latest";
const CDN_STRIKETHROUGH    = "https://cdn.jsdelivr.net/npm/@sotaproject/strikethrough@latest";
const CDN_EMBED            = "https://cdn.jsdelivr.net/npm/@editorjs/embed@latest";
const CDN_ACCORDION        = "https://cdn.jsdelivr.net/npm/editorjs-collapsible-block";
const CDN_INDENT_TUNE_SEB  = "https://cdn.jsdelivr.net/npm/editorjs-indent-tune/dist/bundle.js";

// const CDN_COLUMNS       = "https://cdn.jsdelivr.net/npm/@calumk/editorjs-columns@latest";
// const CDN_BUTTON           = "https://cdn.jsdelivr.net/npm/editorjs-button@latest";
 
async function loadEditorScripts(onReady) {
  try {
     window.process = window.process || { env: {} }; // shim for UMD bundles that reference process.env
    // 1. Load the core Editor.js script first (and wait for it to finish)
    await importScript(CDN_EDITORJS);

    // 2. Load all plugins in parallel (much faster!)
    await Promise.all([
      importScript(CDN_COLOR),
      importScript(CDN_HEADER),
      importScript(CDN_PARA),
      importScript(CDN_LIST),
      importScript(CDN_TABLE),
      importScript(CDN_MARKER),
      importScript(CDN_IMAGE),
      importScript(CDN_QUOTE),
      importScript(CDN_DELIMITER),
      importScript(CDN_CHECKLIST),
      importScript(CDN_UNDERLINE),
      importScript(CDN_INLINE_CODE),
      importScript(CDN_TXT_BLOCKTUNE),
      importScript(CDN_TEXT_STYLE),
      importScript(CDN_NOTICE),
      importScript(CDN_TEXT_VARIANT),
      importScript(CDN_INDENT_TUNE),
      importScript(CDN_WARNING),
      importScript(CDN_ALERT),
      importScript(CDN_STRIKETHROUGH),
      importScript(CDN_EMBED),
      importScript(CDN_ACCORDION),
      importScript(CDN_INDENT_TUNE_SEB),
      // importScript(CDN_COLUMNS),
    ]);

    onReady();
  } catch (error) {
    console.error("[EditorHelpPage] Error loading Editor.js dependencies:", error);
  }
}

class ImageStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block = block;
    this.data  = {
      width:           data?.width           ?? 100,
      border:          data?.border          ?? false,
      borderWidth:     data?.borderWidth     ?? 2,
      borderColor:     data?.borderColor     ?? "#000000",
      cropTop:         data?.cropTop         ?? 0,
      cropRight:       data?.cropRight       ?? 0,
      cropBottom:      data?.cropBottom      ?? 0,
      cropLeft:        data?.cropLeft        ?? 0,
      captionSize:     data?.captionSize     ?? 14,
      captionColor:    data?.captionColor    ?? "#555555",
      captionOverlay:  data?.captionOverlay  ?? false,
    };
  }

  _els() {
    const holder = this.block.holder;
    if (!holder) return {};
    const img      = holder.querySelector("img");
    const wrapper  = img ? img.closest("div") : null;
    const caption  = holder.querySelector(".cdx-input.image-tool__caption") ||
                     holder.querySelector("[data-placeholder='Caption']") ||
                     holder.querySelector("figcaption");
    return { img, wrapper, caption };
  }

  _apply() {
    const { img, wrapper, caption } = this._els();
    if (!img) return;

    img.style.width    = this.data.width + "%";
    img.style.maxWidth = "100%";
    img.style.display  = "block";
    img.style.border = this.data.border
      ? `${this.data.borderWidth}px solid ${this.data.borderColor}`
      : "none";

    const { cropTop: t, cropRight: r, cropBottom: b, cropLeft: l } = this.data;
    img.style.clipPath = (t === 0 && r === 0 && b === 0 && l === 0)
      ? ""
      : `inset(${t}% ${r}% ${b}% ${l}%)`;

    if (caption) {
      caption.style.display   = "block";
      caption.style.width     = this.data.width + "%";
      caption.style.maxWidth  = "100%";
      caption.style.boxSizing = "border-box";

      if (this.data.captionOverlay) {
        this._applyOverlay(img, caption);
      } else {
        this._removeOverlay(img, caption);
        caption.style.fontSize  = `${this.data.captionSize}px`;
        caption.style.color     = this.data.captionColor;
        caption.style.textAlign = "center";
        caption.style.marginTop = "4px";
        caption.style.position  = "";
        caption.style.bottom    = "";
        caption.style.left      = "";
        caption.style.background = "";
        caption.style.padding    = "";
      }
    }
  }

  _applyOverlay(img, caption) {
    let overlayWrap = img.parentElement;
    if (!overlayWrap || !overlayWrap.classList.contains("ejs-img-overlay-wrap")) {
      overlayWrap = document.createElement("div");
      overlayWrap.className = "ejs-img-overlay-wrap";
      overlayWrap.style.cssText = `position:relative;display:inline-block;width:${this.data.width}%;`;
      img.parentNode.insertBefore(overlayWrap, img);
      overlayWrap.appendChild(img);
    }
    overlayWrap.style.width = this.data.width + "%";
    img.style.width  = "100%";
    img.style.display = "block";

    overlayWrap.appendChild(caption);
    caption.style.cssText = `
      position:absolute;
      bottom:0;left:0;right:0;
      width:100%;
      background:rgba(0,0,0,0.5);
      color:#ffffff;
      font-size:${this.data.captionSize}px;
      padding:6px 10px;
      text-align:center;
      box-sizing:border-box;
      margin:0;
    `;
  }

  _removeOverlay(img, caption) {
    const overlayWrap = img.parentElement;
    if (overlayWrap && overlayWrap.classList.contains("ejs-img-overlay-wrap")) {
      const parent = overlayWrap.parentNode;
      parent.insertBefore(img, overlayWrap);
      parent.insertBefore(caption, overlayWrap);
      parent.removeChild(overlayWrap);
      img.style.width = this.data.width + "%";
    }
  }

  wrap(el) { setTimeout(() => this._apply(), 60); return el; }
  save()   { return this.data; }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:8px 12px;min-width:240px;font-size:13px;";

    const slider = (label, key, min, max, unit = "%") => {
      const row = document.createElement("div");
      row.style.marginBottom = "6px";
      const lbl = document.createElement("div");
      lbl.textContent = `${label}: ${this.data[key]}${unit}`;
      lbl.style.marginBottom = "2px";
      const inp = document.createElement("input");
      Object.assign(inp, { type: "range", min: String(min), max: String(max), step: "1", value: this.data[key] });
      inp.style.width = "100%";
      inp.addEventListener("input", e => {
        this.data[key] = +e.target.value;
        lbl.textContent = `${label}: ${this.data[key]}${unit}`;
        this._apply();
      });
      row.append(lbl, inp);
      return row;
    };

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:8px 0 4px;border-top:1px solid #eee;padding-top:6px;";
      d.textContent = text;
      return d;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;";
      const l = document.createElement("span"); l.textContent = labelText;
      const inp = document.createElement("input");
      inp.type  = "color";
      inp.value = this.data[key];
      inp.style.cssText = "width:40px;height:28px;border:none;cursor:pointer;";
      inp.addEventListener("input", e => { this.data[key] = e.target.value; this._apply(); });
      d.append(l, inp);
      return d;
    };

    wrap.append(sep("Size"), slider("Width", "width", 10, 100));
    wrap.append(
      sep("Crop (drag to trim edges)"),
      slider("Top",    "cropTop",    0, 49),
      slider("Right",  "cropRight",  0, 49),
      slider("Bottom", "cropBottom", 0, 49),
      slider("Left",   "cropLeft",   0, 49),
    );

    const resetBtn = document.createElement("button");
    resetBtn.textContent = "Reset crop";
    resetBtn.style.cssText = "margin-top:4px;padding:3px 10px;font-size:12px;cursor:pointer;";
    resetBtn.addEventListener("click", () => {
      this.data.cropTop = this.data.cropRight = this.data.cropBottom = this.data.cropLeft = 0;
      this._apply();
      wrap.querySelectorAll("input[type=range]").forEach(inp => {
        if (["cropTop","cropRight","cropBottom","cropLeft"].includes(inp.dataset.key)) inp.value = 0;
      });
    });
    wrap.append(resetBtn);

    wrap.append(sep("Border"));
    const bRow = document.createElement("div");
    bRow.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px;";
    const bCheck = document.createElement("input");
    bCheck.type = "checkbox"; bCheck.checked = this.data.border;
    const bLabel = document.createElement("label");
    bLabel.textContent = "Show border"; bLabel.style.fontWeight = "600";
    bRow.append(bCheck, bLabel);

    const bOpts = document.createElement("div");
    bOpts.style.display = this.data.border ? "block" : "none";
    bOpts.append(slider("Thickness", "borderWidth", 1, 10, "px"));

    const cRow = document.createElement("div");
    cRow.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:4px;";
    const cLabel = document.createElement("span"); cLabel.textContent = "Color:";
    const cInput = document.createElement("input");
    Object.assign(cInput, { type: "color", value: this.data.borderColor });
    cInput.style.cssText = "width:40px;height:28px;border:none;cursor:pointer;";
    cInput.addEventListener("input", e => { this.data.borderColor = e.target.value; this._apply(); });
    cRow.append(cLabel, cInput);
    bOpts.append(cRow);

    bCheck.addEventListener("change", e => {
      this.data.border = e.target.checked;
      bOpts.style.display = this.data.border ? "block" : "none";
      this._apply();
    });
    wrap.append(bRow, bOpts);
    wrap.append(sep("Caption"));
    wrap.append(slider("Font size", "captionSize", 10, 32, "px"));
    wrap.append(mkColorRow("Text color:", "captionColor"));

    const ovRow = document.createElement("div");
    ovRow.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:6px;";
    const ovCheck = document.createElement("input");
    ovCheck.type = "checkbox"; ovCheck.checked = this.data.captionOverlay;
    const ovLabel = document.createElement("label");
    ovLabel.textContent = "Overlay caption on image";
    ovLabel.style.fontWeight = "600";
    ovRow.append(ovCheck, ovLabel);
    ovCheck.addEventListener("change", e => {
      this.data.captionOverlay = e.target.checked;
      this._apply();
    });
    wrap.append(ovRow);

    return wrap;
  }
}

class TableStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block    = block;
    this._blockEl = null;
    this._observer = null;
    this._colSliderContainer = null;
    this.data = {
      borderWidth: data?.borderWidth ?? 1,
      borderColor: data?.borderColor ?? "#cccccc",
      headingBg:   data?.headingBg   ?? "#f0f0f0",
      tableWidth:  data?.tableWidth  ?? 100,
      colWidths:   data?.colWidths   ?? [],
    };
  }

  _getRoot() {
    const root = this._blockEl || this.block.holder;
    if (!root) return null;
    const tcTable = root.querySelector(".tc-table");
    if (tcTable) return { type: "grid", el: tcTable };
    const table = root.querySelector("table");
    if (table) return { type: "table", el: table };
    return null;
  }

  _apply() {
    const found = this._getRoot();
    if (!found) return;
    const { type, el } = found;

    if (type === "grid") {
      const wrap = el.closest(".tc-wrap") || el;
      wrap.style.width = this.data.tableWidth + "%";
      const rows = el.querySelectorAll(".tc-row");
      rows.forEach((row, rowIdx) => {
        const cells = row.querySelectorAll(".tc-cell");
        cells.forEach((cell, colIdx) => {
          cell.style.border  = `${this.data.borderWidth}px solid ${this.data.borderColor}`;
          cell.style.padding = "6px 8px";
          if (this.data.colWidths[colIdx] !== undefined) {
            cell.style.width = this.data.colWidths[colIdx] + "%";
          }
          if (rowIdx === 0) {
            cell.style.backgroundColor = this.data.headingBg;
            cell.style.fontWeight      = "700";
          }
        });
      });
    } else {
      el.style.borderCollapse = "collapse";
      el.style.width          = this.data.tableWidth + "%";
      const rows = el.querySelectorAll("tr");
      rows.forEach((row, rowIdx) => {
        Array.from(row.querySelectorAll("td, th")).forEach((cell, colIdx) => {
          cell.style.border  = `${this.data.borderWidth}px solid ${this.data.borderColor}`;
          cell.style.padding = "6px 8px";
          if (this.data.colWidths[colIdx] !== undefined) {
            cell.style.width = this.data.colWidths[colIdx] + "%";
          }
          if (rowIdx === 0) {
            cell.style.backgroundColor = this.data.headingBg;
            cell.style.fontWeight      = "700";
          }
        });
      });
    }
  }

  _getColCount() {
    const found = this._getRoot();
    if (!found) return 0;
    const { type, el } = found;
    if (type === "grid") {
      const firstRow = el.querySelector(".tc-row");
      return firstRow ? firstRow.querySelectorAll(".tc-cell").length : 0;
    } else {
      const firstRow = el.querySelector("tr");
      return firstRow ? firstRow.querySelectorAll("td, th").length : 0;
    }
  }

  _getTargetCell() {
    const root = this._blockEl || this.block.holder;
    if (!root) return null;
    return (
      root.querySelector(".tc-cell:focus-within")    ||
      root.querySelector(".tc-cell.tc-cell--selected")||
      this._lastClickedCell                           ||
      root.querySelector(".tc-cell")
    );
  }

  wrap(el) {
    this._blockEl = el;
    this._apply();

    el.addEventListener("mousedown", ev => {
      const cell = ev.target.closest(".tc-cell");
      if (cell) this._lastClickedCell = cell;
    }, true);

    if (this._observer) this._observer.disconnect();
    this._observer = new MutationObserver(() => {
      if (this._getRoot()) {
        this._apply();
        this._buildColSliders();
      }
    });
    this._observer.observe(el, { childList: true, subtree: true, attributes: false });
    setTimeout(() => this._observer && this._observer.disconnect(), 30000);

    return el;
  }

  save() { return this.data; }

  _buildColSliders() {
    const container = this._colSliderContainer;
    if (!container) return;
    container.innerHTML = "";
    const colCount = this._getColCount();
    if (colCount === 0) {
      const msg = document.createElement("div");
      msg.style.cssText = "color:#999;font-size:12px;margin-bottom:6px;";
      msg.textContent = "No table detected yet.";
      container.append(msg);
      return;
    }
    while (this.data.colWidths.length < colCount) {
      this.data.colWidths.push(Math.floor(100 / colCount));
    }
    for (let i = 0; i < colCount; i++) {
      const colIdx = i;
      const val = document.createElement("span");
      val.textContent = this.data.colWidths[colIdx] + "%";
      const slider = document.createElement("input");
      Object.assign(slider, {
        type: "range", min: "5", max: "95", step: "1",
        value: this.data.colWidths[colIdx],
      });
      slider.style.width = "100%";
      slider.addEventListener("input", e => {
        this.data.colWidths[colIdx] = +e.target.value;
        val.textContent = this.data.colWidths[colIdx] + "%";
        this._apply();
      });
      const top = document.createElement("div");
      top.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px;";
      const lbl = document.createElement("span");
      lbl.textContent = `Col ${i + 1}:`;
      top.append(lbl, val);
      const row = document.createElement("div");
      row.style.marginBottom = "8px";
      row.append(top, slider);
      container.append(row);
    }
  }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:10px 14px;min-width:680px;font-size:13px;box-sizing:border-box;";

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:10px 0 6px;border-top:1px solid #eee;padding-top:8px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;color:#555;";
      d.textContent = text;
      return d;
    };

    const mkSliderRow = (labelText, key, min, max, unit) => {
      const val = document.createElement("span");
      val.textContent = this.data[key] + unit;
      const slider = document.createElement("input");
      Object.assign(slider, { type: "range", min: String(min), max: String(max), step: "1", value: this.data[key] });
      slider.style.width = "100%";
      slider.addEventListener("input", e => {
        this.data[key] = +e.target.value;
        val.textContent = this.data[key] + unit;
        this._apply();
      });
      const top = document.createElement("div");
      top.style.cssText = "display:flex;justify-content:space-between;margin-bottom:3px;";
      const lbl = document.createElement("span"); lbl.textContent = labelText;
      top.append(lbl, val);
      const row = document.createElement("div"); row.style.marginBottom = "10px";
      row.append(top, slider);
      return row;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:10px;";
      const l = document.createElement("span");
      l.textContent = labelText;
      l.style.cssText = "flex:1;white-space:nowrap;";
      const swatch = document.createElement("input");
      swatch.type  = "color";
      swatch.value = this.data[key];
      swatch.style.cssText = "width:36px;height:28px;border:1px solid #ccc;border-radius:3px;cursor:pointer;padding:1px;flex-shrink:0;";

      const hex = document.createElement("input");
      hex.type      = "text";
      hex.value     = this.data[key];
      hex.maxLength = 7;
      hex.style.cssText = "width:76px;padding:3px 5px;border:1px solid #ccc;border-radius:3px;font-size:12px;font-family:monospace;flex-shrink:0;";

      const sync = (color) => {
        this.data[key] = color;
        swatch.value   = color;
        hex.value      = color;
        this._apply();
      };

      swatch.addEventListener("input", e => sync(e.target.value));
      hex.addEventListener("change", e => {
        const v = e.target.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v)) sync(v);
        else hex.value = this.data[key];
      });

      d.append(l, hex, swatch);
      return d;
    };

    wrap.append(
      sep("Table"),
      mkSliderRow("Table width:",  "tableWidth",  30, 100, "%"),
      sep("Border"),
      mkSliderRow("Border width:", "borderWidth",   0,   5, "px"),
      mkColorRow("Border color:",  "borderColor"),
      sep("Heading Row"),
      mkColorRow("Heading fill:",  "headingBg"),
    );

    wrap.append(sep("Column Widths"));
    this._colSliderContainer = document.createElement("div");
    wrap.append(this._colSliderContainer);
    setTimeout(() => this._buildColSliders(), 100);

    return wrap;
  }
}

class ChecklistStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block    = block;
    this._blockEl = null;
    this.data = {
      checkboxBorderWidth: data?.checkboxBorderWidth ?? 1,
      checkboxBorderColor: data?.checkboxBorderColor ?? "#cccccc",
      itemBorderWidth:     data?.itemBorderWidth     ?? 0,
      itemBorderColor:     data?.itemBorderColor     ?? "#cccccc",
      itemBorderStyle:     data?.itemBorderStyle     ?? "solid",
      itemBorderRadius:    data?.itemBorderRadius    ?? 4,
    };
  }

  _apply() {
    const root = this._blockEl || this.block?.holder;
    if (!root) return;

    root.style.borderTop    = `${this.data.checkboxBorderWidth}px dashed ${this.data.checkboxBorderColor}`;
    root.style.borderBottom = `${this.data.checkboxBorderWidth}px dashed ${this.data.checkboxBorderColor}`;
    root.style.paddingTop    = "6px";
    root.style.paddingBottom = "6px";

    const checkboxes = root.querySelectorAll(
      ".cdx-checklist__item-checkbox, input[type='checkbox']"
    );
    checkboxes.forEach(cb => {
      cb.style.border       = `${this.data.checkboxBorderWidth}px solid ${this.data.checkboxBorderColor}`;
      cb.style.borderRadius = "3px";
    });
    const items = root.querySelectorAll(".cdx-checklist__item");
    items.forEach(item => {
      if (this.data.itemBorderWidth > 0) {
        item.style.border       = `${this.data.itemBorderWidth}px ${this.data.itemBorderStyle} ${this.data.itemBorderColor}`;
        item.style.borderRadius = `${this.data.itemBorderRadius}px`;
        item.style.padding      = "4px 8px";
        item.style.marginBottom = "4px";
      } else {
        item.style.border       = "none";
        item.style.borderRadius = "0";
        item.style.padding      = "";
        item.style.marginBottom = "";
      }
    });
  }

  wrap(el) {
    this._blockEl = el;
    setTimeout(() => this._apply(), 60);
    const observer = new MutationObserver(() => this._apply());
    observer.observe(el, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
    return el;
  }

  save() { return this.data; }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:8px 12px;min-width:400px;font-size:13px;";

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:8px 0 4px;border-top:1px solid #eee;padding-top:6px;";
      d.textContent = text;
      return d;
    };

    const mkSliderRow = (labelText, key, min, max, unit) => {
      const val = document.createElement("span");
      val.textContent = this.data[key] + unit;
      const slider = document.createElement("input");
      Object.assign(slider, { type: "range", min: String(min), max: String(max), step: "1", value: this.data[key] });
      slider.style.width = "110px";
      slider.addEventListener("input", e => {
        this.data[key] = +e.target.value;
        val.textContent = this.data[key] + unit;
        this._apply();
      });
      const top = document.createElement("div");
      top.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px;";
      const lbl = document.createElement("span"); lbl.textContent = labelText;
      top.append(lbl, val);
      const row = document.createElement("div"); row.style.marginBottom = "8px";
      row.append(top, slider);
      return row;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px;";
      const l = document.createElement("span");
      l.textContent = labelText;
      l.style.flex = "1";

      const swatch = document.createElement("input");
      swatch.type  = "color";
      swatch.value = this.data[key];
      swatch.style.cssText = "width:36px;height:28px;border:1px solid #ccc;border-radius:3px;cursor:pointer;padding:1px;flex-shrink:0;";

      const hex = document.createElement("input");
      hex.type      = "text";
      hex.value     = this.data[key];
      hex.maxLength = 7;
      hex.style.cssText = "width:72px;padding:3px 5px;border:1px solid #ccc;border-radius:3px;font-size:12px;font-family:monospace;flex-shrink:0;";

      const sync = (color) => {
        this.data[key] = color;
        swatch.value   = color;
        hex.value      = color;
        this._apply();
      };
      swatch.addEventListener("input", e => sync(e.target.value));
      hex.addEventListener("change", e => {
        const v = e.target.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v)) sync(v);
        else hex.value = this.data[key];
      });

      d.append(l, hex, swatch);
      return d;
    };

    const mkStyleSelect = (labelText, key, options) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px;";
      const l = document.createElement("span");
      l.textContent = labelText;
      l.style.flex = "1";
      const sel = document.createElement("select");
      sel.style.cssText = "padding:3px 6px;border:1px solid #ccc;border-radius:3px;font-size:12px;";
      options.forEach(opt => {
        const o = document.createElement("option");
        o.value = opt; o.textContent = opt;
        if (this.data[key] === opt) o.selected = true;
        sel.appendChild(o);
      });
      sel.addEventListener("change", e => { this.data[key] = e.target.value; this._apply(); });
      d.append(l, sel);
      return d;
    };

    wrap.append(
      sep("Top/Bottom Lines & Checkbox Border"),
      mkSliderRow("Line thickness:", "checkboxBorderWidth", 1, 5, "px"),
      mkColorRow("Line / box color:", "checkboxBorderColor"),

      sep("Item Row Border"),
      mkSliderRow("Thickness:", "itemBorderWidth", 0, 5, "px"),
      mkColorRow("Color:", "itemBorderColor"),
      mkStyleSelect("Style:", "itemBorderStyle", ["solid", "dashed", "dotted", "double"]),
      mkSliderRow("Radius:", "itemBorderRadius", 0, 16, "px"),
    );

    return wrap;
  }
}
class ButtonStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block = block;
    this._blockEl = null;
    this.data = {
      bgColor: data?.bgColor ?? "#555555",
      textColor: data?.textColor ?? "#ffffff",
      align: data?.align ?? "left",
    };
  }

  _apply() {
    const root = this._blockEl || this.block?.holder;
    if (!root) return;
    root.style.display = "flex";
    root.style.justifyContent =
      this.data.align === "center" ? "center" :
        this.data.align === "right" ? "flex-end" : "flex-start";
    const btn = root.querySelector(".any-button, a.any-button, button.any-button, [class*='btn']");
    if (btn) {
      btn.style.backgroundColor = this.data.bgColor;
      btn.style.color = this.data.textColor;
      btn.style.border = "none";
      btn.style.padding = "8px 20px";
      btn.style.borderRadius = "4px";
      btn.style.cursor = "pointer";
      btn.style.display = "inline-block";
      btn.style.textDecoration = "none";
    }
  }

  wrap(el) {
    this._blockEl = el;
    setTimeout(() => this._apply(), 80);
    const observer = new MutationObserver(() => this._apply());
    observer.observe(el, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 30000);
    return el;
  }

  save() { return this.data; }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:8px 12px;min-width:230px;font-size:13px;";

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:8px 0 4px;border-top:1px solid #eee;padding-top:6px;";
      d.textContent = text;
      return d;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;";
      const l = document.createElement("span"); l.textContent = labelText;
      const inp = document.createElement("input");
      inp.type = "color";
      inp.value = this.data[key];
      inp.style.cssText = "width:40px;height:28px;border:none;cursor:pointer;";
      inp.addEventListener("input", e => { this.data[key] = e.target.value; this._apply(); });
      d.append(l, inp);
      return d;
    };

    wrap.append(sep("Position"));
    const posRow = document.createElement("div");
    posRow.style.cssText = "display:flex;gap:6px;margin-bottom:8px;";
    ["left", "center", "right"].forEach(pos => {
      const btn = document.createElement("button");
      btn.textContent = pos.charAt(0).toUpperCase() + pos.slice(1);
      btn.style.cssText = `flex:1;padding:4px;font-size:12px;cursor:pointer;border:2px solid ${this.data.align === pos ? '#0070ff' : '#ccc'};border-radius:3px;background:${this.data.align === pos ? '#e8f0ff' : '#fff'};`;
      btn.addEventListener("click", () => {
        this.data.align = pos;
        posRow.querySelectorAll("button").forEach((b, i) => {
          const p = ["left", "center", "right"][i];
          b.style.borderColor = this.data.align === p ? "#0070ff" : "#ccc";
          b.style.background = this.data.align === p ? "#e8f0ff" : "#fff";
        });
        this._apply();
      });
      posRow.append(btn);
    });
    wrap.append(posRow);
    wrap.append(
      sep("Colors"),
      mkColorRow("Background:", "bgColor"),
      mkColorRow("Text color:", "textColor"),
    );
    wrap.append(sep("Edit"));
    const editBtn = document.createElement("button");
    editBtn.textContent = "✏️ Edit button text / URL";
    editBtn.style.cssText = "width:100%;padding:5px;font-size:12px;cursor:pointer;margin-top:2px;";
    editBtn.addEventListener("click", () => {
      const root = this._blockEl || this.block?.holder;
      const anyBtn = root?.querySelector(".any-button, a.any-button");
      if (anyBtn) anyBtn.click();
    });
    wrap.append(editBtn);
    return wrap;
  }
}

class QuoteStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block    = block;
    this._blockEl = null;
    this._observer = null;
    this.data = {
      borderWidth:   data?.borderWidth   ?? 3,
      borderColor:   data?.borderColor   ?? "#cccccc",
      fillColor:     data?.fillColor     ?? "#f9f9f9",
      textSize:      data?.textSize      ?? 16,
      textColor:     data?.textColor     ?? "#333333",
      captionSize:   data?.captionSize   ?? 13,
      captionColor:  data?.captionColor  ?? "#888888",
    };
  }

  _apply() {
    const root = this._blockEl || this.block?.holder;
    if (!root) return;


    const quoteWrap =
      root.querySelector(".cdx-quote")          || 
      root.querySelector("blockquote")           ||
      root.querySelector(".cdx-block");

    if (quoteWrap) {
      quoteWrap.style.borderLeft      = `${this.data.borderWidth}px solid ${this.data.borderColor}`;
      quoteWrap.style.backgroundColor = this.data.fillColor;
      quoteWrap.style.paddingLeft     = "12px";
      quoteWrap.style.paddingTop      = "8px";
      quoteWrap.style.paddingBottom   = "8px";
      quoteWrap.style.margin          = "0";
      quoteWrap.style.borderRadius    = "0 2px 2px 0";
    }

    const textEl =
      root.querySelector(".cdx-quote__text")                         ||
      root.querySelector("[data-placeholder='Enter a quote']")       ||
      root.querySelector(".cdx-input:first-of-type");

    if (textEl) {
      textEl.style.fontSize   = `${this.data.textSize}px`;
      textEl.style.color      = this.data.textColor;
      textEl.style.lineHeight = "1.5";
    }

    const captionEl =
      root.querySelector(".cdx-quote__caption")                            ||
      root.querySelector("[data-placeholder=\"Quote's author\"]")          ||
      root.querySelector("[data-placeholder='Quote\\'s author']")          ||
      root.querySelector(".cdx-input:last-of-type");

    if (captionEl && captionEl !== textEl) {
      captionEl.style.fontSize   = `${this.data.captionSize}px`;
      captionEl.style.color      = this.data.captionColor;
      captionEl.style.fontStyle  = "italic";
      captionEl.style.marginTop  = "4px";
    }
  }

  wrap(el) {
    this._blockEl = el;

    this._apply();

    if (this._observer) this._observer.disconnect();
    this._observer = new MutationObserver(() => {
      const hasQuoteDom =
        el.querySelector(".cdx-quote")    ||
        el.querySelector(".cdx-input")    ||
        el.querySelector("blockquote");
      if (hasQuoteDom) this._apply();
    });
    this._observer.observe(el, { childList: true, subtree: true });

    setTimeout(() => this._observer && this._observer.disconnect(), 30000);

    return el;
  }

  save() { return this.data; }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:8px 12px;min-width:240px;font-size:13px;";

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:8px 0 4px;border-top:1px solid #eee;padding-top:6px;";
      d.textContent = text;
      return d;
    };

    const mkSliderRow = (labelText, key, min, max, unit) => {
      const val = document.createElement("span");
      val.textContent = this.data[key] + unit;
      const slider = document.createElement("input");
      Object.assign(slider, { type: "range", min: String(min), max: String(max), step: "1", value: this.data[key] });
      slider.style.width = "100%";
      slider.addEventListener("input", e => {
        this.data[key] = +e.target.value;
        val.textContent = this.data[key] + unit;
        this._apply();
      });
      const top = document.createElement("div");
      top.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px;";
      const lbl = document.createElement("span"); lbl.textContent = labelText;
      top.append(lbl, val);
      const row = document.createElement("div"); row.style.marginBottom = "8px";
      row.append(top, slider);
      return row;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;";
      const l = document.createElement("span"); l.textContent = labelText;
      const inp = document.createElement("input");
      Object.assign(inp, { type: "color", value: this.data[key] });
      inp.style.cssText = "width:40px;height:28px;border:none;cursor:pointer;";
      inp.addEventListener("input", e => { this.data[key] = e.target.value; this._apply(); });
      d.append(l, inp);
      return d;
    };

    wrap.append(
      sep("Left Border"),
      mkSliderRow("Thickness:", "borderWidth", 1, 10, "px"),
      mkColorRow("Color:", "borderColor"),

      sep("Box Fill"),
      mkColorRow("Fill color:", "fillColor"),

      sep("Quote Text"),
      mkSliderRow("Size:", "textSize", 10, 32, "px"),
      mkColorRow("Color:", "textColor"),

      sep("Caption (Author)"),
      mkSliderRow("Size:", "captionSize", 10, 24, "px"),
      mkColorRow("Color:", "captionColor"),
    );

    return wrap;
  }
}

class DelimiterStyleTune {
  static get isTune() { return true; }

  constructor({ data, block }) {
    this.block    = block;
    this._blockEl = null;
    this._observer = null;
    this.data = {
      symbolColor:    data?.symbolColor    ?? "#b5adad",
      symbolSize:     data?.symbolSize     ?? 18,
      symbolSpacing:  data?.symbolSpacing  ?? 8,
      lineColor:      data?.lineColor      ?? "#b3b3b3",
      lineThickness:  data?.lineThickness  ?? 2,
    };
  }

  _apply() {
    const root = this._blockEl || this.block?.holder;
    if (!root) return;
    const el =
      root.querySelector(".ce-delimiter")     ||
      root.querySelector(".cdx-delimiter")    ||
      root.querySelector("[class*='delimiter']");

    if (!el) return;
    const isLine = el.tagName === "HR" || el.querySelector("hr") || el.style.borderBottom;

    if (isLine || el.tagName === "HR") {
      el.style.borderColor    = this.data.lineColor;
      el.style.borderTopColor = this.data.lineColor;
      el.style.borderWidth    = `${this.data.lineThickness}px`;
    } else {
      el.style.color          = this.data.symbolColor;
      el.style.fontSize       = `${this.data.symbolSize}px`;
      el.style.letterSpacing  = `${this.data.symbolSpacing}px`;
    }
  }

  wrap(el) {
    this._blockEl = el;
    setTimeout(() => this._apply(), 80);

    if (this._observer) this._observer.disconnect();
    this._observer = new MutationObserver(() => this._apply());
    this._observer.observe(el, { childList: true, subtree: true, attributes: true });
    setTimeout(() => this._observer && this._observer.disconnect(), 30000);

    return el;
  }

  save() { return this.data; }

  render() {
    const wrap = document.createElement("div");
    wrap.style.cssText = "padding:8px 12px;min-width:260px;font-size:13px;";

    const sep = (text) => {
      const d = document.createElement("div");
      d.style.cssText = "font-weight:700;margin:8px 0 4px;border-top:1px solid #eee;padding-top:6px;";
      d.textContent = text;
      return d;
    };

    const mkSliderRow = (labelText, key, min, max, unit) => {
      const val = document.createElement("span");
      val.textContent = this.data[key] + unit;
      const slider = document.createElement("input");
      Object.assign(slider, { type: "range", min: String(min), max: String(max), step: "1", value: this.data[key] });
      slider.style.width = "100%";
      slider.addEventListener("input", e => {
        this.data[key] = +e.target.value;
        val.textContent = this.data[key] + unit;
        this._apply();
      });
      const top = document.createElement("div");
      top.style.cssText = "display:flex;justify-content:space-between;margin-bottom:2px;";
      const lbl = document.createElement("span"); lbl.textContent = labelText;
      top.append(lbl, val);
      const row = document.createElement("div"); row.style.marginBottom = "8px";
      row.append(top, slider);
      return row;
    };

    const mkColorRow = (labelText, key) => {
      const d = document.createElement("div");
      d.style.cssText = "display:flex;align-items:center;gap:8px;margin-bottom:8px;";
      const l = document.createElement("span");
      l.textContent = labelText;
      l.style.flex = "1";

      const swatch = document.createElement("input");
      swatch.type  = "color";
      swatch.value = this.data[key];
      swatch.style.cssText = "width:36px;height:28px;border:1px solid #ccc;border-radius:3px;cursor:pointer;padding:1px;flex-shrink:0;";

      const hex = document.createElement("input");
      hex.type      = "text";
      hex.value     = this.data[key];
      hex.maxLength = 7;
      hex.style.cssText = "width:72px;padding:3px 5px;border:1px solid #ccc;border-radius:3px;font-size:12px;font-family:monospace;flex-shrink:0;";

      const sync = (color) => {
        this.data[key] = color;
        swatch.value   = color;
        hex.value      = color;
        this._apply();
      };
      swatch.addEventListener("input", e => sync(e.target.value));
      hex.addEventListener("change", e => {
        const v = e.target.value.trim();
        if (/^#[0-9a-fA-F]{6}$/.test(v)) sync(v);
        else hex.value = this.data[key];
      });

      d.append(l, hex, swatch);
      return d;
    };

    wrap.append(
      sep("Symbol (★ / —)"),
      mkColorRow("Color:", "symbolColor"),
      mkSliderRow("Size:", "symbolSize", 10, 40, "px"),
      mkSliderRow("Letter spacing:", "symbolSpacing", 0, 30, "px"),

      sep("Line Delimiter"),
      mkColorRow("Line color:", "lineColor"),
      mkSliderRow("Line thickness:", "lineThickness", 1, 8, "px"),
    );

    return wrap;
  }
}

class TextClassTool {
  static get isInline() { return true; }

  static get sanitize() {
    return {
      span: { class: true },
      p:    { class: true },
      div:  { class: true }
    };
  }

  constructor({ api, config }) {
    this.api          = api;
    this.button       = null;
    this.dropdown     = null;
    this.classOptions = config.classOptions || [];
  }

  render() {
    this.button = document.createElement("button");
    this.button.type = "button";
    this.button.innerHTML = "<b>CSS</b>";
    this.button.classList.add(this.api.styles.inlineToolButton);
    return this.button;
  }

  renderActions() {
    this.dropdown = document.createElement("select");
    this.dropdown.style.cssText = "padding:4px;margin:0 10px;border:1px solid #ccc;border-radius:4px;";
    const defaultOpt = document.createElement("option");
    defaultOpt.text = "Choose Style...";
    defaultOpt.value = "";
    defaultOpt.disabled = true;
    defaultOpt.selected = true;
    this.dropdown.appendChild(defaultOpt);
    this.classOptions.forEach(opt => {
      const option = document.createElement("option");
      option.value = opt.className;
      option.text  = opt.label;
      this.dropdown.appendChild(option);
    });
    this.dropdown.addEventListener("change", (e) => {
      this.applyStyle(e.target.value);
      this.dropdown.value = "";
    });
    return this.dropdown;
  }

  surround(range) {}

  checkState() {
    // Find if the cursor is inside a span
    const span = this.api.selection.findParentTag('SPAN');

    // 1. If there's no span at all, do nothing.
    if (!span) return false;

    // 2. THE FIX: Check if the span actually has a CSS class!
    // If it only has a style attribute (like color: red) from your Color Picker, 
    // span.className will be empty, and this will safely return false.
    if (span.className && span.className.trim() !== '') {
      
      // (If you have code here that updates your dropdown's selected value, keep it!)
      
      return true; // Yes, this is a CSS span, activate the dropdown!
    }

    return false; // It's just a color span, ignore it!
  }

  applyStyle(className) {
    if (!className) return;
    const classArray = className.split(' ').filter(c => c.trim() !== '');
    const existingTag =
      this.api.selection.findParentTag("SPAN") ||
      this.api.selection.findParentTag("P");
    if (existingTag) {
      classArray.forEach(c => { existingTag.classList.toggle(c); });
      const hasNoClasses = existingTag.classList.length === 0;
      const hasNoStyles = !existingTag.hasAttribute('style') || existingTag.getAttribute('style').trim() === '';
      if (existingTag.tagName === 'SPAN' && hasNoClasses && hasNoStyles) {
        const textNode = document.createTextNode(existingTag.textContent);
        existingTag.parentNode.replaceChild(textNode, existingTag);
      }
    } else {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;
      const range   = selection.getRangeAt(0);
      const newSpan = document.createElement("span");
      newSpan.classList.add(...classArray);
      newSpan.appendChild(range.extractContents());
      range.insertNode(newSpan);
    }
    this.api.inlineToolbar.close();
  }
}

class NativeColorTool {
  static get isInline() { return true; }

  static get sanitize() {
    return { span: { style: true } };
  }

  constructor({ api }) {
    this.api = api;
    this.button = null;
    this.activeSpan = null;
  }

  render() {
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.classList.add(this.api.styles.inlineToolButton);
    this.button.innerHTML = '🎨';
    return this.button;
  }

  getOrCreateSpan() {
    let span = this.api.selection.findParentTag('SPAN');
    if (!span) {
      const selection = window.getSelection();
      if (!selection.rangeCount) return null;
      const range = selection.getRangeAt(0);
      span = document.createElement('span');
      span.appendChild(range.extractContents());
      range.insertNode(span);
      selection.removeAllRanges();
      const newRange = document.createRange();
      newRange.selectNodeContents(span);
      selection.addRange(newRange);
    }
    return span;
  }

  renderActions() {
    const wrapper = document.createElement('div');
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.gap = '10px';
    wrapper.style.padding = '0 10px';

    const textContainer = document.createElement('div');
    textContainer.title = "Text Color";
    textContainer.innerHTML = '<span style="font-size:12px; font-weight:bold; margin-right:4px;">A</span>';
    const textColorInput = document.createElement('input');
    textColorInput.type = 'color';
    textColorInput.classList.add('native-color-picker');
    textColorInput.addEventListener('click', () => { this.activeSpan = this.getOrCreateSpan(); });
    textColorInput.addEventListener('input', (e) => {
      if (!this.activeSpan) this.activeSpan = this.getOrCreateSpan();
      if (this.activeSpan) this.activeSpan.style.color = e.target.value;
    });
    textContainer.appendChild(textColorInput);

    const bgContainer = document.createElement('div');
    bgContainer.title = "Background Color";
    bgContainer.innerHTML = '<span style="font-size:12px; font-weight:bold; margin-right:4px;">bg</span>';
    const bgColorInput = document.createElement('input');
    bgColorInput.type = 'color';
    bgColorInput.classList.add('native-color-picker');
    bgColorInput.addEventListener('click', () => { this.activeSpan = this.getOrCreateSpan(); });
    bgColorInput.addEventListener('input', (e) => {
      if (!this.activeSpan) this.activeSpan = this.getOrCreateSpan();
      if (this.activeSpan) this.activeSpan.style.backgroundColor = e.target.value;
    });
    bgContainer.appendChild(bgColorInput);

    wrapper.appendChild(textContainer);
    wrapper.appendChild(bgContainer);
    return wrapper;
  }

  clear() { this.activeSpan = null; }
  surround(range) {}
  checkState() {
    const span = this.api.selection.findParentTag('SPAN');

    if (!span) return false;

    // Only claim this span if it has a text color OR a background color
    if (span.style.color || span.style.backgroundColor) {
      return true;
    }

    return false;
  }
}

class AdvancedButton {
  static get toolbox() {
    return {
      title: 'Button',
      icon: '<span style="font-weight: 800; font-size: 12px; font-family: monospace;">BTN</span>'
    };
  }

  constructor({ data, api }) {
    this.api = api;
    this.data = {
      text: data.text || '',
      link: data.link || '',
      alignment: data.alignment || 'center',
      backgroundColor: data.backgroundColor || '#007bff',
      textColor: data.textColor || '#ffffff'
    };
    this.wrapper = undefined;
    this.textInput = undefined;
    this.linkInput = undefined;
  }

  validateText(text) { return text.trim().length > 0; }

  validateLink(url) {
    if (url.trim().length === 0) return false;
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    return urlPattern.test(url);
  }

  render() {
    this.wrapper = document.createElement('div');
    this.wrapper.classList.add('custom-button-container');

    this.textInput = document.createElement('div');
    this.textInput.classList.add('btn-text-input');
    this.textInput.contentEditable = true;
    this.textInput.innerText = this.data.text;
    this.textInput.dataset.placeholder = 'Enter Button Text...';

    const textError = document.createElement('div');
    textError.classList.add('error-message');
    textError.innerText = "Button text cannot be empty.";

    if (!this.data.text) this.textInput.dataset.empty = "true";
    this.textInput.addEventListener('input', () => {
      this.textInput.dataset.empty = this.textInput.textContent.trim() === '' ? "true" : "false";
    });
    this.textInput.addEventListener('blur', () => {
      if (!this.validateText(this.textInput.innerText)) {
        this.textInput.classList.add('input-error');
        textError.style.display = 'block';
      } else {
        this.textInput.classList.remove('input-error');
        textError.style.display = 'none';
      }
    });

    this.linkInput = document.createElement('input');
    this.linkInput.classList.add('btn-url-input');
    this.linkInput.placeholder = 'Paste URL (https://...)';
    this.linkInput.value = this.data.link;

    const linkError = document.createElement('div');
    linkError.classList.add('error-message');
    linkError.innerText = "Please enter a valid URL.";

    this.linkInput.addEventListener('blur', (e) => {
      let currentUrl = e.target.value.trim();
      if (currentUrl && !currentUrl.startsWith('http://') && !currentUrl.startsWith('https://')) {
        currentUrl = 'https://' + currentUrl;
        e.target.value = currentUrl;
      }
      if (!this.validateLink(currentUrl)) {
        this.linkInput.classList.add('input-error');
        linkError.style.display = 'block';
      } else {
        this.linkInput.classList.remove('input-error');
        linkError.style.display = 'none';
      }
    });

    this.wrapper.appendChild(this.textInput);
    this.wrapper.appendChild(textError);
    this.wrapper.appendChild(this.linkInput);
    this.wrapper.appendChild(linkError);
    this.updateUI();
    return this.wrapper;
  }

  renderSettings() {
    const wrapper = document.createElement('div');
    wrapper.classList.add('button-settings-wrapper');

    const alignGroup = document.createElement('div');
    alignGroup.classList.add('alignment-group');
    const alignments = [
      { name: 'left',   icon: '<span style="font-size: 12px; font-weight: bold;">Left</span>' },
      { name: 'center', icon: '<span style="font-size: 12px; font-weight: bold;">Center</span>' },
      { name: 'right',  icon: '<span style="font-size: 12px; font-weight: bold;">Right</span>' }
    ];
    alignments.forEach(tune => {
      const btn = document.createElement('div');
      btn.classList.add(this.api.styles.settingsButton);
      if (this.data.alignment === tune.name) btn.classList.add(this.api.styles.settingsButtonActive);
      btn.innerHTML = tune.icon;
      btn.addEventListener('click', () => {
        this.data.alignment = tune.name;
        this.updateUI();
        this.api.toolbar.toggleBlockSettings();
        this.api.toolbar.toggleBlockSettings();
      });
      alignGroup.appendChild(btn);
    });

    const bgGroup = document.createElement('div');
    bgGroup.classList.add('color-group');
    bgGroup.innerHTML = '<span>Background Color:</span>';
    const bgPicker = document.createElement('input');
    bgPicker.type = 'color';
    bgPicker.value = this.data.backgroundColor;
    bgPicker.classList.add('settings-color-picker');
    bgPicker.addEventListener('input', (e) => { this.data.backgroundColor = e.target.value; this.updateUI(); });
    bgGroup.appendChild(bgPicker);

    const textGroup = document.createElement('div');
    textGroup.classList.add('color-group');
    textGroup.innerHTML = '<span>Text Color:</span>';
    const textPicker = document.createElement('input');
    textPicker.type = 'color';
    textPicker.value = this.data.textColor;
    textPicker.classList.add('settings-color-picker');
    textPicker.addEventListener('input', (e) => { this.data.textColor = e.target.value; this.updateUI(); });
    textGroup.appendChild(textPicker);

    wrapper.appendChild(alignGroup);
    wrapper.appendChild(bgGroup);
    wrapper.appendChild(textGroup);
    return wrapper;
  }

  updateUI() {
    if (!this.wrapper || !this.textInput) return;
    if (this.data.alignment === 'left') this.wrapper.style.alignItems = 'flex-start';
    else if (this.data.alignment === 'right') this.wrapper.style.alignItems = 'flex-end';
    else this.wrapper.style.alignItems = 'center';
    this.textInput.style.backgroundColor = this.data.backgroundColor;
    this.textInput.style.color = this.data.textColor;
  }

  save(blockContent) {
    return {
      text: this.textInput.innerText.trim(),
      link: this.linkInput.value.trim(),
      alignment: this.data.alignment,
      backgroundColor: this.data.backgroundColor,
      textColor: this.data.textColor
    };
  }

  validate(savedData) {
    return this.validateText(savedData.text) && this.validateLink(savedData.link);
  }
}

const EditorJsRenderer = ({
  id, currentUserEmail, content, onContentChange, required, dataType, dataId, formData, restrictMediaFiles,
  formFn={}, uploadPrivacyType='private', isImgCloneEnabled, isCodeViewEnabled, disabled
}) => {
  const holderRef = useRef(null);
  const editorRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadEditorScripts(() => {
      console.log("[EditorJS] Scripts loaded:", {
        EditorJS:      !!window.EditorJS,
        Header:        !!window.Header,
        List:          !!window.EditorjsList,
        Table:         !!window.Table,
        Marker:        !!window.Marker,
        ImageTool:     !!window.ImageTool,
        ColorPlugin:   !!window.ColorPlugin,
        Quote:         !!window.Quote,
        Delimiter:     !!window.Delimiter,
        Warning:       !!window.Warning,
        Alert:         !!window.Alert,
        Strikethrough: !!window.Strikethrough,
        Embed:         !!window.Embed,
        Accordion:     !!window.AccordionBlock,
        IndentTuneSeb: !!window.IndentTune,
      });
      setReady(true);
    });
  }, []);

  const imageUploader = {
    uploadByFile(file) {
      if (file.size / 1024 / 1024 > 2)
        return Promise.reject(new Error("Image must be under 2 MB"));
        
      const imageData = { fileable_type: dataType, fileable_id: dataId, is_rich_text: true };
      return FileUploadService.uploadFileToUrl(file, uploadPrivacyType, imageData).then(res => {
        const fu  = res.data.file_upload;
        const url = Cs.getIconByType(fu.url || fu.file);
        return { success: 1, file: { url } };
      });
    },
    uploadByUrl: url => Promise.resolve({ success: 1, file: { url } }),
  };

  class InlineImageTool {
    static get isInline() {
      return true;
    }

    constructor({ api }) {
      this.api = api;
      this.button = null;
    }

    render() {
      this.button = document.createElement('button');
      this.button.type = 'button';
      this.button.classList.add(this.api.styles.inlineToolButton);
      this.button.innerHTML = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>`;
      
      return this.button;
    }

    surround(range) {
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = 'image/*';

      fileInput.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 1. Instantly close the toolbar and show the loader where the cursor was
        this.api.inlineToolbar.close();
        const loaderElement = this.showLoader(range);

        // 2. Perform the actual Server Upload
        try {
          // Example Fetch Request
          const data = await imageUploader.uploadByFile(file)
          
          if (data.file?.url) {
            // 3. Success! Replace the loader with the actual image
            this.replaceLoaderWithImage(loaderElement, data.file?.url);
          } else {
            // 4. Handle API error (remove loader, maybe alert user)
            loaderElement.remove();
            alert('Upload failed: Invalid response from server.');
          }
        } catch (error) {
          // 4. Handle Network error
          loaderElement.remove();
          console.error('Error uploading image:', error);
          alert('Network error during upload.');
        }
      };

      fileInput.click();
    }

    showLoader(range) {
      const wrapper = document.createElement('span');
      wrapper.classList.add('ce-inline-image-loader-wrapper');
      wrapper.contentEditable = false; // Prevent user from typing inside the loader
      
      wrapper.innerHTML = `
        <span class="ce-inline-image-loader"></span>
        <span style="margin-left: 6px;">Uploading...</span>
      `;

      // Insert the loader into the table cell
      range.insertNode(wrapper);
      
      return wrapper;
    }

    // --- NEW: Helper method to swap the spinner for the image ---
    replaceLoaderWithImage(loaderElement, imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.style.maxWidth = '100%';
      img.style.borderRadius = '4px';
      img.style.marginTop = '10px';
      img.style.marginBottom = '10px';

      // Safely replace the loader DOM node with the image DOM node
      if (loaderElement && loaderElement.parentNode) {
        loaderElement.parentNode.replaceChild(img, loaderElement);
      }
    }

    checkState() {
      return false;
    }
  }

  useEffect(() => {
    if (!ready || !holderRef.current) return;

    if (editorRef.current) {
      try { editorRef.current.destroy(); } catch (e) {}
      editorRef.current = null;
    }

    if (!window.EditorJS) {
      console.error("[EditorJS] window.EditorJS is undefined — CDN failed.");
      return;
    }

    const data = typeof content === 'string' ? Cs.htmlToEditorJS(content) : content;
    console.log("[EditorJS] Converted blocks:", data);

    class CustomList extends window.EditorjsList {
      static get sanitize() {
        const defaultRules = window.EditorjsList.sanitize || {};
        return Object.assign({}, defaultRules, {
          li: { class: true },
          ul: { class: true },
          ol: { class: true },
          span: { class: true, style: true }
        });
      }
    }

    class CustomParagraph extends window.Paragraph {
      static get sanitize() {
        return {
          p: { class: true, style: true },
          span: { class: true, style: true },
          b: true,
          i: true,
          a: { href: true, target: true, rel: true },
          mark: { class: true }
        };
      }
    }
    class CustomTable extends window.Table {
      
      static get sanitize() {
        const defaultRules = window.Table.sanitize || {};
        
        // Merge them with our new permission to allow <img> tags
        return Object.assign({}, defaultRules, {
          // Allow images with basic attributes
          img: { 
            src: true, 
            width: true, 
            height: true, 
            alt: true,
            style: true // Optional: if you want to allow resizing via styles
          }
        });
      }
    }

    // Inherit everything from the official Checklist plugin
    class StyledChecklist extends window.Checklist {
      
      constructor(args) {
        super(args);
        
        // 1. Add borderRadius to your state data
        this.styleData = {
          color: args.data.color || '#388bfd',         
          borderThickness: args.data.borderThickness || 2, 
          size: args.data.size || 20,
          // If it's not set, default to 4px. We check for undefined in case they explicitly set it to 0.
          borderRadius: args.data.borderRadius !== undefined ? args.data.borderRadius : 4 
        };
      }

      render() {
        this.wrapper = super.render();
        this.wrapper.classList.add('custom-styled-checklist');
        this.updateStyles();
        return this.wrapper;
      }

      renderSettings() {
        const settingsWrapper = document.createElement('div');

        // A. Color Picker
        const colorRow = document.createElement('div');
        colorRow.classList.add('chk-setting-row');
        colorRow.innerHTML = '<span>Box Color</span>';
        const colorInput = document.createElement('input');
        colorInput.type = 'color';
        colorInput.value = this.styleData.color;
        colorInput.classList.add('m-l-15');
        colorInput.addEventListener('input', (e) => {
          this.styleData.color = e.target.value;
          this.updateStyles();
        });
        colorRow.appendChild(colorInput);

        // B. Border Thickness
        const borderRow = document.createElement('div');
        borderRow.classList.add('chk-setting-row');
        borderRow.innerHTML = '<span>Border (px)</span>';
        const borderInput = document.createElement('input');
        borderInput.type = 'range';
        borderInput.min = '1';
        borderInput.max = '5';
        borderInput.value = this.styleData.borderThickness;
        borderInput.addEventListener('input', (e) => {
          this.styleData.borderThickness = e.target.value;
          this.updateStyles();
        });
        borderRow.appendChild(borderInput);

        // C. Size Slider
        const sizeRow = document.createElement('div');
        sizeRow.classList.add('chk-setting-row');
        sizeRow.innerHTML = '<span>Size (px)</span>';
        const sizeInput = document.createElement('input');
        sizeInput.type = 'range';
        sizeInput.min = '14'; 
        sizeInput.max = '40';
        sizeInput.value = this.styleData.size;
        sizeInput.addEventListener('input', (e) => {
          this.styleData.size = e.target.value;
          this.updateStyles();
        });
        sizeRow.appendChild(sizeInput);

        // D. Border Radius Slider
        const radiusRow = document.createElement('div');
        radiusRow.classList.add('chk-setting-row');
        radiusRow.innerHTML = '<span>Radius (px)</span>';
        const radiusInput = document.createElement('input');
        radiusInput.type = 'range';
        radiusInput.min = '0'; // 0 = Sharp Square
        radiusInput.max = '20'; // If this reaches half the size, it becomes a perfect circle!
        radiusInput.value = this.styleData.borderRadius;
        radiusInput.addEventListener('input', (e) => {
          this.styleData.borderRadius = e.target.value;
          this.updateStyles();
        });
        radiusRow.appendChild(radiusInput);

        // Append everything
        settingsWrapper.appendChild(colorRow);
        settingsWrapper.appendChild(borderRow);
        settingsWrapper.appendChild(sizeRow);
        settingsWrapper.appendChild(radiusRow); 

        return settingsWrapper;
      }

      // 2. Map the state to the new CSS variable
      updateStyles() {
        if (!this.wrapper) return;
        this.wrapper.style.setProperty('--chk-color', this.styleData.color);
        this.wrapper.style.setProperty('--chk-border', `${this.styleData.borderThickness}px`);
        this.wrapper.style.setProperty('--chk-size', `${this.styleData.size}px`);
        this.wrapper.style.setProperty('--chk-radius', `${this.styleData.borderRadius}px`); // NEW
      }

      // 3. Save it to JSON
      save(blockContent) {
        const defaultData = super.save(blockContent);
        return {
          ...defaultData,
          color: this.styleData.color,
          borderThickness: parseInt(this.styleData.borderThickness),
          size: parseInt(this.styleData.size),
          borderRadius: parseInt(this.styleData.borderRadius) // NEW
        };
      }
    }

    const tools = {};

    if (window.Header) {
      tools.header = {
        class: window.Header,
        config: { levels: [1, 2, 3, 4], defaultLevel: 2 },
        inlineToolbar: true,
        tunes: ["alignment", "noticeTune", "textVariant", "indentTune"],
      };
    }

    if (CustomParagraph) {
      tools.paragraph = {
        class: CustomParagraph,
        inlineToolbar: true,
        tunes: ["alignment", "noticeTune", "textVariant", "indentTune"],
        config: { preserveBlank: true },
      };
    }

    if (window.EditorJSTextStyle) {
      tools.fontStyle = {
        class: window.EditorJSTextStyle,
        config: {
          fontSizeEnabled:   true,
          fontFamilyEnabled: true,
          fontSizes: [
            { label: "12px", value: "12px" },
            { label: "14px", value: "14px" },
            { label: "16px", value: "16px" },
            { label: "18px", value: "18px" },
            { label: "20px", value: "20px" },
            { label: "22px", value: "22px" },
            { label: "24px", value: "24px" },
            { label: "26px", value: "26px" },
            { label: "28px", value: "28px" },
          ],
          fontFamilies: [
            { label: "Arial",          value: "Arial" },
            { label: "Georgia",        value: "Georgia" },
            { label: "Courier New",    value: "Courier New" },
            { label: "Verdana",        value: "Verdana" },
            { label: "Roboto Regular", value: "roboto_regular" },
            { label: "Roboto Medium",  value: "roboto_medium" },
            { label: "Roboto Bold",    value: "roboto_bold" },
            { label: "Roboto Black",   value: "roboto_black" },
          ],
          defaultFontSize:   "14px",
          defaultFontFamily: "Roboto Regular",
        },
      };
    }

    if (TextClassTool) {
      tools.textStyle = {
        class: TextClassTool,
        config: {
          classOptions: [
            { label: 'Alert primary',  className: 'ejs-alert ejs-alert-primary' },
            { label: 'Text center',    className: 'text-center' },
            { label: 'Text left',      className: 'text-left' },
            { label: 'Text right',     className: 'text-right' },
            { label: 'Badge',          className: 'badge-2' },
            { label: 'Dashed',         className: 'border-dashed-sm' },
            { label: 'Padding 15',     className: 'p-15' }
          ]
        }
      };
    }

    if(InlineImageTool){
      tools.InlineImageTool = { class: InlineImageTool };
    }

    if (NativeColorTool) {
      tools.colorPicker = { class: NativeColorTool };
    }

    if (StyledChecklist) {
      tools.checklist = {
        class: StyledChecklist,
        inlineToolbar: true
      };
    }

    if (CustomList) {
      tools.list = { class: CustomList, inlineToolbar: true };
    }

    if (window.Marker) {
      tools.marker = { class: window.Marker, shortcut: "CMD+SHIFT+M" };
    }

    if (CustomTable) {
      tools.table      = { class: CustomTable, inlineToolbar: true, tunes: ["tableStyle", "noticeTune", "textVariant", "indentTune"] };
      tools.tableStyle = { class: TableStyleTune };
    }

    if (window.ImageTool) {
      tools.image      = { class: window.ImageTool, tunes: ["imageStyle"], config: { uploader: imageUploader } };
      tools.imageStyle = { class: ImageStyleTune };
    }

    /*if (window.ColorPicker) {
      tools.Color = {
        class: window.ColorPicker.default,
        colors: [
          "#FF1300", "#EC7878", "#9C27B0", "#673AB7", "#3F51B5",
          "#0070FF", "#03A9F4", "#00BCD4", "#4CAF50", "#8BC34A", "#000000",
        ],
      };
    }*/

    if (window.Quote) {
      tools.quote = {
        class: window.Quote,
        inlineToolbar: true,
        shortcut: "CMD+SHIFT+O",
        config: {
          quotePlaceholder:   "Enter a quote",
          captionPlaceholder: "Quote's author",
        },
        tunes: ["quoteStyle"],
      };
      tools.quoteStyle = { class: QuoteStyleTune };
    }

    if (window.Delimiter) {
      tools.delimiter = {
        class: window.Delimiter,
        config: {
          styleOptions:         ["star", "dash", "line"],
          defaultStyle:         "star",
          lineWidthOptions:     [8, 15, 25, 35, 50, 60, 100],
          defaultLineWidth:     25,
          lineThicknessOptions: [1, 2, 3, 4, 5, 6],
          defaultLineThickness: 2,
        },
        tunes: ["delimiterStyle"],
      };
      tools.delimiterStyle = { class: DelimiterStyleTune };
    }

    if (window.Underline) {
      tools.underline = { class: window.Underline, shortcut: "CMD+U" };
    }

    if (window.InlineCode) {
      tools.inlineCode = { class: window.InlineCode, shortcut: "CMD+SHIFT+C" };
    }

    if (window.AlignmentBlockTune) {
      tools.alignment = {
        class: window.AlignmentBlockTune,
        config: {
          default: "left",
          blocks: { header: "left", list: "left" },
        },
      };
    }

    if (window.NoticeTune)      { tools.noticeTune  = window.NoticeTune; }
    if (window.TextVariantTune) { tools.textVariant  = window.TextVariantTune; }

    if (window.IndentPlugin) {
      tools.indentTune = {
        class: window.IndentPlugin,
        version: window.EditorJS.version,
      };
      if (!document.getElementById("ejs-indent-styles")) {
        const style = document.createElement("style");
        style.id = "ejs-indent-styles";
        style.textContent = `...`;
        document.head.appendChild(style);
      }
      if (!document.getElementById("ejs-table-zindex-fix")) {
        const style = document.createElement("style");
        style.id = "ejs-table-zindex-fix";
        style.textContent = `
          .tc-wrap { position: relative; z-index: 0; }
          .tc-toolbar, .tc-popover, .tc-settings,
          .tc-row-adder, .tc-column-adder,
          .tc-add-column, .tc-add-row,
          .tc-toolbox, .tc-toolbox--column, .tc-toolbox--row {
            position: relative; z-index: 9999 !important;
          }
          .ce-block--selected .ce-block__content,
          .ce-block .ce-block__content { overflow: visible !important; }
          .cdx-block, .tc-table { overflow: visible !important; }
        `;
        document.head.appendChild(style);
      }
    }

    if (window.Warning) {
      tools.warning = {
        class: window.Warning,
        inlineToolbar: true,
        shortcut: "CMD+SHIFT+W",
        config: { titlePlaceholder: "Title", messagePlaceholder: "Message" },
      };
    }

    if (window.Alert) {
      tools.alert = {
        class: window.Alert,
        inlineToolbar: true,
        config: { defaultType: "primary", messagePlaceholder: "Type your alert message here..." },
      };
    }

    if (window.Strikethrough) {
      tools.strikethrough = { class: window.Strikethrough };
    }

    if (AdvancedButton) {
       /*if (window.AnyButton) {
      tools.AnyButton = {
        class: window.AnyButton,
        inlineToolbar: false,
        config: {
          css: {
            btnColor: "btn--gray",
          },
          buttonText: "Set",
          textValidation: (text) => {
            if (!text || text.trim().length === 0) {
              console.warn("[AnyButton] Button text is empty");
              return false;
            }
            return true;
          },
          linkValidation: (link) => {
            if (!link || link.trim().length === 0) {
              console.warn("[AnyButton] Link URL is empty");
              return false;
            }
            return true;
          },
        },
        tunes: ["buttonStyle"],
      };
      tools.buttonStyle = { class: ButtonStyleTune };
    }*/

      tools.button = AdvancedButton;
    }

    const IndentTuneSeb = window.IndentTune?.default || window.IndentTune;
    if (IndentTuneSeb) {
      tools.indentTuneSeb = {
        class: IndentTuneSeb,
        config: {
          indentSize:  24,
          maxIndent:   8,
          multiblock:  true,
          tuneName:    "indentTuneSeb",
          autoIndent:  true,
        },
      };
    }

    if (window.Embed) {
      class EmbedWithToolbox extends window.Embed {
        static get toolbox() {
          return {
            title: 'Embed',
            icon: '<svg width="17" height="15" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M8 5l-7 7 7 7M16 5l7 7-7 7"/></svg>'
          };
        }
      }
      tools.embed = {
        class: EmbedWithToolbox,
        inlineToolbar: true,
        config: {
          services: {
            youtube: true, vimeo: true, figma: true,
            codepen: true, github: true, reddit: true,
            pinterest: true, miro: true, coub: true,
          },
        },
      };
    }

    if (window.AccordionBlock) {
      tools.accordion = {
        class: window.AccordionBlock,
        config: { defaultExpanded: true, maxBlockCount: 10, disableAnimation: false },
      };
    }

    /*
    // Columns — disabled, uncomment when ready
    if (window.editorjsColumns) {
      tools.columns = {
        class: window.editorjsColumns,
        config: {
          EditorJsLibrary: window.EditorJS,
          tools: {
            header:    window.Header,
            delimiter: window.Delimiter,
            paragraph: window.Paragraph,
          },
        },
      };
    }
    */

    try {
      editorRef.current = new window.EditorJS({
        holder:   holderRef.current,
        readOnly: false,
        data,
        tools,
        onReady: () => console.log("[EditorJS] Instance ready for holderId:", id),
        onChange: (api, event) => {
          api.saver.save().then((outputData) => {
            console.log('Article data: ', outputData);
            onContentChange(outputData);
          }).catch((error) => {
            console.log('Saving failed: ', error);
          });
        }
      });
    } catch (err) {
      console.error("[EditorJS] Init error:", err);
    }

    return () => {
      if (editorRef.current) {
        try { editorRef.current.destroy(); } catch (e) {}
        editorRef.current = null;
      }
    };
  }, [ready]);

  return (
    <div>
      {!ready && <Spinner />}
      <div ref={holderRef} id={id} style={{ minHeight: "40px" }} />
    </div>
  );
};

export default EditorJsRenderer;